import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { shallow } from 'zustand/shallow';
import { useFilterEvents } from '../useFilterEvents';

type SortOption<T> = {
  value: T;
  label: string;
};

type SortByMobileStoreProps<TSortValue> = <T>(
  selector: (state: { sortBy: TSortValue; handleSortBy: (val: TSortValue) => void }) => T,
  equalityFn?: (a: T, b: T) => boolean,
) => T;

type Props<TSortValue> = {
  sortOptions: readonly SortOption<TSortValue>[];
  useStore: SortByMobileStoreProps<TSortValue>;
  title?: string;
  defaultExpanded?: boolean;
  actionSection: string;
  pageIdentifier: string;
};

export const SortByMobile = <TSortValue extends string>({
  sortOptions,
  useStore,
  title = 'SORT BY',
  defaultExpanded = true,
  actionSection,
  pageIdentifier,
}: Props<TSortValue>) => {
  const { sortBy, handleSortBy } = useStore(
    ({ sortBy, handleSortBy }) => ({
      sortBy,
      handleSortBy,
    }),
    shallow,
  );

  const { sortBySelectEvent } = useFilterEvents({
    actionSection,
    pageIdentifier,
  });

  const handleSortByChange = (value: TSortValue) => {
    setTimeout(() => {
      handleSortBy(value);

      sortBySelectEvent({
        additional_attributes: {
          sort_by_value: sortOptions.find(option => option.value === value)?.label,
        },
      });
    }, 100);
  };

  return (
    <Accordion
      disableGutters
      elevation={0}
      square
      defaultExpanded={defaultExpanded}
      sx={{
        border: 0,
        '&.Mui-expanded': {
          borderBottom: 0,
        },
        '&.MuiAccordion-root': {
          borderBottom: 0,
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          '&:hover': {
            backgroundColor: 'transparent',
          },
          borderBottom: '1px solid',
          borderBottomColor: 'neutral.lighter',
          px: 3,
          mb: 0.5,
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: '#0069B9',
          },
          '&:focus:hover': {
            outline: '2px solid !important', // overriding default MUI CSS that has !important
            outlineColor: '#0069B9 !important', // overriding default MUI CSS that has !important
          },
        }}
      >
        <Typography sx={{ fontSize: 14, color: 'text.secondary', fontWeight: 600 }}>
          {title}
        </Typography>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0 }}>
        <List disablePadding>
          {Object.values(sortOptions).map(({ label, value }) => (
            <ListItemButton
              key={value}
              selected={sortBy === value}
              sx={{
                px: 3,
                '&.Mui-selected': {
                  backgroundColor: 'transparent',
                  ':hover': { backgroundColor: 'neutral.lightest' },
                  ':focus-visible': { backgroundColor: 'neutral.lightest' },
                },
              }}
              onClick={() => handleSortByChange(value)}
            >
              <ListItemText
                primary={label}
                sx={{
                  '& .MuiListItemText-primary': {
                    fontSize: 14,
                    color: 'text.secondary',
                  },
                }}
              />
              {sortBy === value && (
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <CheckIcon fontSize="small" sx={{ color: 'greens.main' }} />
                </ListItemIcon>
              )}
            </ListItemButton>
          ))}
        </List>
      </AccordionDetails>
    </Accordion>
  );
};
